import React, { useMemo }    from "react"
import { ResponsivePie }     from "@nivo/pie"
import { ResponsiveBar }     from "@nivo/bar"
import { ResponsiveLine }    from "@nivo/line"
import { showMoney }         from "@helpers/javascript/date_helpers"
import { truncateLabel }     from "./widget_helpers"

const Widget = ({ widgetId, availableWidgetDefinitions, rows }) => {
  const template = useMemo(() => {
    return availableWidgetDefinitions.find(def => def.identifier === widgetId)
  }, [widgetId, availableWidgetDefinitions])
  
  if (!template) {
    return (
      <div>
        <h5 className = "mb-4 text-center">
          {I18n.t('export.widgets.no_data')}
        </h5>
      </div>
    )
  }

  const { title, chart_type, transformer, chart_config = {}, value_format, currency } = template
  const { keys, indexBy } = chart_config

  const chartData = useMemo(() => {
    if (!rows || rows.length === 0) return []
    try {
      const processedData = transformer(rows)
      
      switch (chart_type) {
        case "pie":
          return processedData.map(([key, value]) => ({
            id:    key,
            label: key,
            value: Number(value) || 0,
          }))
        case "bar":
          return processedData.map(([key, count]) => ({
            name:  key,
            value: Number(count) || 0,
          }))
        case "grouped_bar":
          return processedData
        case "line":
          return [
            {
              id: title,
              data: processedData.map(([key, value]) => ({
                x: key,
                y: Number(value) || 0,
              })),
            },
          ]
        default:
          return []
      }
    } catch (error) {
      console.error(`Error processing data for widget ${widgetId}:`, error)
      return []
    }
  }, [rows, transformer, chart_type, title, widgetId])

  const formatValue = (value, format, currency) => {
    if (!format) return value
    
    switch (format) {
      case 'currency':
        return showMoney({ amount: value, currency: currency })
      
      case 'percentage':
        return `${(100 * value) / 100}%`
      
      default:
        return value
    }
  }

  const renderChart = () => {
    if (!chartData || chartData.length === 0) {
      return (
        <div className = "d-flex a-i-center j-c-center h-100">
          {I18n.t("dashboard.no_data")}
        </div>
      )
    }

    switch (chart_type) {
      case "pie":
        return (
          <ResponsivePie
            data                    = {chartData}
            margin                  = {{ top: 20, right: 80, bottom: 80, left: 80 }}
            innerRadius             = {0.5}
            padAngle                = {0.7}
            cornerRadius            = {3}
            activeOuterRadiusOffset = {8}
            colors                  = {{ scheme: "paired" }}
            borderWidth             = {1}
            borderColor             = {{ from: "color", modifiers: [["darker", 0.2]] }}
            arcLinkLabelsSkipAngle  = {10}
            arcLinkLabelsTextColor  = "#333333"
            arcLinkLabelsThickness  = {2}
            arcLinkLabelsColor      = {{ from: "color" }}
            arcLabelsSkipAngle      = {10}
            arcLabelsTextColor      = {{ from: "color", modifiers: [["darker", 2]] }}
            motionConfig            = "gentle"
            animate
            arcLabel                = {d => formatValue(d.value, value_format, currency)}
            tooltip = {({ datum }) => (
              <div className = "widget-label bg-white p-2 border rounded">
                <strong>{datum.label}</strong>: {formatValue(datum.value, value_format, currency)}
              </div>
            )}
          />
        )
      case "bar":
        return (
          <ResponsiveBar
            data            = {chartData}
            keys            = {["value"]}
            indexBy         = "name"
            margin          = {{ top: 20, right: 30, bottom: 80, left: 80 }}
            padding         = {0.3}
            valueScale      = {{ type: "linear" }}
            indexScale      = {{ type: "band", round: true }}
            colors          = {{ scheme: "nivo" }}
            borderColor     = {{ from: "color", modifiers: [["darker", 1.6]] }}
            axisTop         = {null}
            axisRight       = {null}
            axisBottom      = {{
              tickSize:       5,
              tickPadding:    5,
              tickRotation:   -45,
              legendPosition: "middle",
              legendOffset:   50,
              format: (value) => truncateLabel(value, 18)
            }}
            axisLeft        = {{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legendPosition: "middle",
              legendOffset: -60,
              format: value => formatValue(value, value_format, currency)
            }}
            labelSkipWidth  = {120}
            labelSkipHeight = {12}
            labelTextColor  = {{ from: "color", modifiers: [["darker", 1.6]] }}
            motionConfig    = "gentle"
            animate
            label           = {d => formatValue(d.value, value_format, currency)}
            tooltip         = {({ id, value, indexValue }) => (
              <div className = "widget-label bg-white p-2 border rounded">
                <span><strong>{indexValue}</strong>: {formatValue(value, value_format, currency)}</span>
              </div>
            )}
          />
        )
      case "grouped_bar":
        return (
          <ResponsiveBar
            data            = {chartData}
            keys            = {keys || Object.keys(chartData[0] || {}).filter(k => k !== (indexBy || 'group'))}
            indexBy         = {indexBy || "group"}
            margin          = {{ top: 20, right: 130, bottom: 80, left: 80 }}
            padding         = {0.3}
            valueScale      = {{ type: "linear" }}
            indexScale      = {{ type: "band", round: true }}
            colors          = {{ scheme: "nivo" }}
            borderColor     = {{ from: "color", modifiers: [["darker", 1.6]] }}
            axisTop         = {null}
            axisRight       = {null}
            axisBottom      = {{
              tickSize:       5,
              tickPadding:    5,
              tickRotation:   -45,
              legendPosition: "middle",
              legendOffset:   50,
              format: (value) => truncateLabel(value, 18)
            }}
            axisLeft        = {{
              tickSize:       5,
              tickPadding:    5,
              tickRotation:   0,
              legendPosition: "middle",
              legendOffset:   -60,
              format: value => formatValue(value, value_format, currency)
            }}
            labelSkipWidth  = {12}
            labelSkipHeight = {12}
            labelTextColor  = {{ from: "color", modifiers: [["darker", 1.6]] }}
            motionConfig    = "gentle"
            legends         = {[
              {
                dataFrom:      "keys",
                anchor:        "bottom-right",
                direction:     "column",
                justify:       false,
                translateX:    120,
                translateY:    0,
                itemsSpacing:  2,
                itemWidth:     100,
                itemHeight:    20,
                itemDirection: "left-to-right",
                itemOpacity:   0.85,
                symbolSize:    20,
                effects: [
                  {
                    on:    "hover",
                    style: {itemOpacity: 1}
                  }
                ]
              }
            ]}
            animate
            tooltip = {({ id, value, indexValue }) => (
              <div className = "widget-label bg-white p-2 border rounded">
                <span><strong>{indexValue}</strong> - {id}: {formatValue(value, value_format, currency)}</span>
              </div>
            )}
          />
        )
      case "line":
        return (
          <ResponsiveLine
            data              = {chartData}
            margin            = {{ top: 20, right: 30, bottom: 80, left: 80 }}
            xScale            = {{ type: "point" }}
            yScale            = {{
              type:    "linear",
              min:     "auto",
              max:     "auto",
              stacked: false,
              reverse: false
            }}
            yFormat           = {value => formatValue(value, value_format, currency)}
            curve             = "cardinal"
            axisTop           = {null}
            axisRight         = {null}
            axisBottom        = {{
              orient:         "bottom",
              tickSize:       5,
              tickPadding:    5,
              tickRotation:   -45,
              legendOffset:   50,
              legendPosition: "middle",
              format: (value) => truncateLabel(value, 18)
            }}
            axisLeft          = {{
              orient:         "left",
              tickSize:       5,
              tickPadding:    5,
              tickRotation:   0,
              legendOffset:   -60,
              legendPosition: "middle",
              format: value => formatValue(value, value_format, currency)
            }}
            colors            = {{ scheme: "nivo" }}
            pointSize         = {8}
            pointColor        = {{ theme: "background" }}
            pointBorderWidth  = {2}
            pointBorderColor  = {{ from: "serieColor" }}
            pointLabelYOffset = {-12}
            useMesh           = {true}
            motionConfig      = "gentle"
            animate
            tooltip = {({ point }) => (
              <div className = "widget-label bg-white p-2 border rounded">
                <span><strong>{point.data.x}</strong>: {formatValue(point.data.y, value_format, currency)}</span>
              </div>
            )}
          />
        )
    }
  }

  return (
    <div className = "text-center widget-content">
      <h5 className = "widget-title mb-4 mt-6">{title}</h5>
      <div className = "widget-chart" style = {{ height: "400px", width: "100%" }} >
        {renderChart()}
      </div>
    </div>
  )
}

export default Widget